import React from 'react';
import { LoadingIndicator, OutlinedTextInput, Text } from 'library';
import { Autocomplete, styled } from '@mui/material';
import { ProviderSelection } from 'parent-portal/forms/JobRequestTypes';
import { useJobFormContext } from 'parent-portal/forms/JobFormContext';
import { workerStatusStyle } from './utilities';
import type { ExtendedProviderSelection } from './utilities';

interface Props {
    selected: ProviderSelection[];
    onChange: (selectedProviderUserIds: ProviderSelection[]) => void;
    multiple?: boolean;
    clearOtherInput?: () => void;
}

export default function PastWorkersInput({ selected, onChange, multiple = true, clearOtherInput }: Props) {
    const { pastWorkers, loadingPastWorkers } = useJobFormContext();

    const pastWorkerOptions: ExtendedProviderSelection[] = pastWorkers.map((worker) => ({
        id: worker.id,
        name: `${worker.first_name} ${worker.last_name}`,
        status: worker.status,
        sanitizedStatus: worker.status ? `(${worker.status.replaceAll('_', ' ').capitalize()})` : '',
    }));

    const onDropdownSelect = (value: ProviderSelection | ProviderSelection[] | null) => {
        if (!multiple && !!value) clearOtherInput?.();

        if (!value) {
            onChange([]);
        } else if (Array.isArray(value)) {
            const values = value.map((it: { id: number }) => it.id);
            onChange(pastWorkerOptions.filter((it: { id: number }) => values.includes(it.id)));
        } else {
            const choice = pastWorkerOptions.find((it: { id: number }) => it.id === value.id);
            onChange(choice ? [choice] : []);
        }
    };

    const renderContent = () => {
        if (loadingPastWorkers) {
            return <LoadingIndicator />;
        }
        if (pastWorkers.length === 0) {
            return <Text>It looks like you have not set any preferred workers.</Text>;
        }
        return (
            <Autocomplete
                multiple={multiple}
                value={multiple ? selected.map((it) => it) : selected?.[0] ?? null}
                options={pastWorkerOptions}
                onChange={(_, value) => onDropdownSelect(value)}
                getOptionLabel={(option) => `${option.name}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionDisabled={(option: ExtendedProviderSelection) => option.status === 'out_of_compliance'}
                renderOption={(props, option: ExtendedProviderSelection) => (
                    <li {...props}>
                        <StyledOption>
                            <Text>{option.name}</Text>
                            {option.sanitizedStatus && (
                                <Text variant="caption" textStyle={{ ...workerStatusStyle(option.status), marginLeft: 5 }}>
                                    {option.sanitizedStatus}
                                </Text>
                            )}
                        </StyledOption>
                    </li>
                )}
                renderInput={(params) => (
                    <OutlinedTextInput
                        {...params}
                        style={{ marginTop: 10 }}
                        variant="outlined"
                        helperText="Select workers to recommend for this job."
                        placeholder="Click here to search for past workers"
                    />
                )}
            />
        );
    };

    return (
        <>
            <Spacer />
            <Text variant="body1" bold>
                Past Workers
            </Text>
            {renderContent()}
        </>
    );
}

const Spacer = styled('div')({
    marginTop: 15,
});

const StyledOption = styled('div')({
    display: 'flex',
    alignItems: 'baseline',
});
